import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { FormikProps } from 'formik';

import {
    AggregateOperationDto,
    AggregateOperationDtoAggregateFunctionEnum,
    ColumnDto,
    ColumnDtoTypeEnum,
    DataSourceDto,
} from '../../../../../../../typescript-axios';

// ─── Types ────────────────────────────────────────────────────────────────────

interface StepGroupByAggregationsProps {
    formik: FormikProps<{ dataSource: DataSourceDto }>;
    aggregatableOptions: Record<string, ColumnDto>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const NUMERIC_FUNCTIONS = Object.values(AggregateOperationDtoAggregateFunctionEnum);

const NON_NUMERIC_FUNCTIONS = [
    AggregateOperationDtoAggregateFunctionEnum.Count,
    AggregateOperationDtoAggregateFunctionEnum.CountDistinct,
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getAllowedFunctions = (colDef?: ColumnDto) =>
    colDef?.type === ColumnDtoTypeEnum.Number ? NUMERIC_FUNCTIONS : NON_NUMERIC_FUNCTIONS;

const findAliasForOp = (
    op: AggregateOperationDto,
    aggregatableOptions: Record<string, ColumnDto>
): string | undefined =>
    Object.entries(aggregatableOptions).find(
        ([, dto]) => dto.table === op.column.table && dto.column === op.column.column
    )?.[0];

// ─── Shared label style ───────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-color-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 6,
};

// ─── AggregationDialog ────────────────────────────────────────────────────────

interface AggregationDialogProps {
    visible: boolean;
    initial: { alias: string; op: AggregateOperationDto } | null;
    aggregatableOptions: Record<string, ColumnDto>;
    existingAliases: string[];
    onConfirm: (alias: string, op: AggregateOperationDto) => void;
    onHide: () => void;
}

const AggregationDialog: React.FC<AggregationDialogProps> = ({
    visible,
    initial,
    aggregatableOptions,
    existingAliases,
    onConfirm,
    onHide,
}) => {
    const { t } = useTranslation();

    const buildDefault = useCallback((): { alias: string; op: AggregateOperationDto } => {
        const firstAlias = Object.keys(aggregatableOptions)[0] ?? '';
        const firstCol = aggregatableOptions[firstAlias];
        const allowedFns = getAllowedFunctions(firstCol);
        const fn = allowedFns.includes(AggregateOperationDtoAggregateFunctionEnum.Count)
            ? AggregateOperationDtoAggregateFunctionEnum.Count
            : allowedFns[0];
        return {
            alias: firstAlias,
            op: {
                aggregate_function: fn,
                column: {
                    table: firstCol?.table,
                    column: firstCol?.column,
                    type: ColumnDtoTypeEnum.Number,
                },
            },
        };
    }, [aggregatableOptions]);

    const [draft, setDraft] = useState<{ alias: string; op: AggregateOperationDto }>(() =>
        initial ? { ...initial } : buildDefault()
    );

    React.useEffect(() => {
        if (visible) setDraft(initial ? { ...initial } : buildDefault());
    }, [visible, initial, buildDefault]);

    const colDef = aggregatableOptions[draft.alias];
    const allowedFunctions = getAllowedFunctions(colDef);

    const isRestricted = allowedFunctions.length < NUMERIC_FUNCTIONS.length;

    const columnOptions = Object.keys(aggregatableOptions).map((key) => ({
        label: key,
        value: key,
    }));

    const functionOptions = allowedFunctions.map((fn) => ({
        label: fn,
        value: fn,
    }));

    const handleColumnChange = (alias: string) => {
        const col = aggregatableOptions[alias];
        const allowed = getAllowedFunctions(col);
        const fn =
            allowed.includes(draft.op.aggregate_function)
                ? draft.op.aggregate_function
                : allowed.includes(AggregateOperationDtoAggregateFunctionEnum.Count)
                    ? AggregateOperationDtoAggregateFunctionEnum.Count
                    : allowed[0];

        setDraft({
            alias,
            op: {
                aggregate_function: fn,
                column: { table: col.table, column: col.column, type: ColumnDtoTypeEnum.Number },
            },
        });
    };

    const handleFunctionChange = (fn: AggregateOperationDtoAggregateFunctionEnum) => {
        setDraft((d) => ({ ...d, op: { ...d.op, aggregate_function: fn } }));
    };

    const isDuplicate = !initial && existingAliases.includes(draft.alias);

    const isValid = !!draft.alias && !!draft.op.aggregate_function && !isDuplicate;

    const footer = (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
                label={t('pages.dashboards.delete.confirm_reject')}
                severity="secondary"
                outlined
                size="small"
                type="button"
                onClick={onHide}
            />
            <Button
                label={initial ? t('pages.dashboards.datasource.update_aggregation') : t('pages.dashboards.datasource.add_aggregation')}
                icon="pi pi-check"
                size="small"
                type="button"
                disabled={!isValid}
                onClick={() => onConfirm(draft.alias, draft.op)}
            />
        </div>
    );

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={
                <span style={{ fontSize: 14, fontWeight: 600 }}>
                    {initial
                        ? t('pages.dashboards.datasource.edit_aggregation')
                        : t('pages.dashboards.datasource.add_aggregation')}
                </span>
            }
            footer={footer}
            style={{ width: 480 }}
            modal
            draggable={false}
            resizable={false}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingTop: 4 }}>

                {/* Column */}
                <div>
                    <label style={labelStyle}>
                        {t('pages.dashboards.datasource.column')}
                    </label>
                    <Dropdown
                        value={draft.alias}
                        options={columnOptions}
                        onChange={(e) => handleColumnChange(e.value)}
                        style={{ width: '100%' }}
                        filter={columnOptions.length > 8}
                    />
                </div>

                {/* Function */}
                <div>
                    <label style={labelStyle}>
                        {t('pages.dashboards.datasource.function')}
                    </label>
                    <Dropdown
                        value={draft.op.aggregate_function}
                        options={functionOptions}
                        onChange={(e) => handleFunctionChange(e.value)}
                        style={{ width: '100%' }}
                        tooltip={
                            isRestricted
                                ? t('pages.dashboards.datasource.count_only_non_numeric')
                                : undefined
                        }
                        tooltipOptions={{ position: 'top' }}
                    />
                </div>
            </div>
        </Dialog>
    );
};

// ─── StepGroupByAggregations ──────────────────────────────────────────────────

export const StepGroupByAggregations: React.FC<StepGroupByAggregationsProps> = ({
    formik,
    aggregatableOptions,
}) => {
    const { t } = useTranslation();

    const [dialogVisible, setDialogVisible] = useState(false);
    const [editIndex, setEditIndex] = useState<number | null>(null);

    const aggregateOps: AggregateOperationDto[] =
        formik.values.dataSource.query_definition.group_by?.aggregate_operations ?? [];

    const setOps = useCallback(
        (newOps: AggregateOperationDto[]) => {
            formik.setFieldValue(
                'dataSource.query_definition.group_by.aggregate_operations',
                newOps.length > 0 ? newOps : undefined
            );
        },
        [formik]
    );

    const existingAliases = useMemo(
        () =>
            aggregateOps
                .map((op) => findAliasForOp(op, aggregatableOptions))
                .filter((alias): alias is string => alias !== undefined),
        [aggregateOps, aggregatableOptions]
    );

    const openAdd = () => { setEditIndex(null); setDialogVisible(true); };
    const openEdit = (i: number) => { setEditIndex(i); setDialogVisible(true); };

    const handleConfirm = (alias: string, op: AggregateOperationDto) => {
        if (editIndex === null) {
            setOps([...aggregateOps, op]);
        } else {
            setOps(aggregateOps.map((existing, i) => (i === editIndex ? op : existing)));
        }
        setDialogVisible(false);
    };

    const handleRemove = (i: number, e: React.MouseEvent) => {
        e.stopPropagation();
        setOps(aggregateOps.filter((_, idx) => idx !== i));
    };

    // Resolve the alias + colDef for the item being edited
    const editInitial = editIndex !== null
        ? (() => {
            const op = aggregateOps[editIndex];
            if (!op) return null;
            const alias = findAliasForOp(op, aggregatableOptions);
            if (!alias) return null;
            return { alias, op };
        })()
        : null;

    return (
        <div className="field col-12">
            <label htmlFor="input-aggregate-columns" className="font-medium">
                {t('pages.dashboards.datasource.select_columns')}
            </label>

            {/* ── Aggregation table ── */}
            {aggregateOps.length > 0 && (
                <div style={{ marginBottom: 10, border: '1px solid var(--surface-200)', borderRadius: 6, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                            <tr style={{ background: 'var(--surface-50)', borderBottom: '1px solid var(--surface-200)' }}>
                                <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-color-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11 }}>
                                    {t('pages.dashboards.datasource.column')}
                                </th>
                                <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-color-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11 }}>
                                    {t('pages.dashboards.datasource.function')}
                                </th>
                                <th style={{ width: 60 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {aggregateOps.map((op, i) => {
                                const alias = findAliasForOp(op, aggregatableOptions);
                                if (!alias) return null;

                                const isLast = i === aggregateOps.length - 1;

                                return (
                                    <tr
                                        key={i}
                                        onClick={() => openEdit(i)}
                                        title="Click to edit"
                                        style={{
                                            borderBottom: isLast ? 'none' : '1px solid var(--surface-100)',
                                            cursor: 'pointer',
                                            transition: 'background 0.12s',
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-50)')}
                                        onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                                    >
                                        <td style={{ padding: '7px 10px', fontWeight: 500, color: 'var(--text-color)' }}>
                                            <i className="pi pi-chart-bar" style={{ fontSize: 10, opacity: 0.45, marginRight: 6 }} />
                                            {alias}
                                        </td>
                                        <td style={{ padding: '7px 10px', color: 'var(--primary-600, #2563eb)', fontWeight: 600 }}>
                                            {op.aggregate_function}
                                        </td>
                                        <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                                            <i
                                                className="pi pi-trash"
                                                onClick={(e) => handleRemove(i, e)}
                                                title="Remove"
                                                style={{ fontSize: 11, opacity: 0.45, cursor: 'pointer', padding: 4, borderRadius: 4, transition: 'opacity 0.1s' }}
                                                onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.opacity = '1')}
                                                onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.opacity = '0.45')}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── Add button ── */}
            <Button
                label={t('pages.dashboards.datasource.add_aggregation')}
                icon="pi pi-plus"
                severity="secondary"
                outlined
                size="small"
                type="button"
                onClick={openAdd}
                disabled={Object.keys(aggregatableOptions).length === 0}
            />

            {/* ── Dialog ── */}
            <AggregationDialog
                visible={dialogVisible}
                initial={editInitial}
                aggregatableOptions={aggregatableOptions}
                existingAliases={existingAliases}
                onConfirm={handleConfirm}
                onHide={() => setDialogVisible(false)}
            />
        </div>
    );
};